/**
 * NotificationsContext.tsx
 *
 * Backs the notification bell in LifestyleDashboard: reminders sent by the
 * backend "measurement due soon" tick (Api/services/reminders.py), 10
 * minutes before a scheduled programme measurement is due.
 *
 * Data flow
 * 1) `loadNotifications()` fetches the current list from the backend on
 *    mount (see LifestyleDashboard/AppRouter wiring).
 * 2) A live reminder arrives over the socket ('notification' event, wired in
 *    WebSocketProvider) and is prepended via `addPushedNotification()` so an
 *    already-open tab updates without a refetch.
 * 3) `markRead()` optimistically flips the local row, then persists it.
 * 4) `deleteOne()` removes a single notification, optimistically, the same
 *    way `markRead()` does.
 * 5) `clearAll()` deletes every notification for the current user, only
 *    clearing local state once the backend confirms it.
 *
 * `measurementName`/`programmeName` are language-neutral facts, not display
 * text -- LifestyleDashboard renders the actual title/body through i18next
 * (`notifications.reminderTitle`/`reminderBody`) at render time, so a
 * notification always shows in whichever language is active *now*, however
 * old it is and whatever language was active when it was created.
 */
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  ReactNode,
} from 'react';
import client from '../api/client';

export interface Notification {
  id: number;
  measurementId: number;
  programmeId: number;
  measurementName: string;
  programmeName: string;
  dueAt: string;
  readAt: string | null;
}

interface NotificationsContextType {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
  loadNotifications: () => Promise<void>;
  markRead: (id: number) => Promise<void>;
  deleteOne: (id: number) => Promise<void>;
  clearAll: () => Promise<void>;
  addPushedNotification: (notification: Notification) => void;
}

const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined);

interface NotificationsProviderProps {
  children: ReactNode;
}

export const NotificationsProvider: React.FC<NotificationsProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadNotifications = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await client.get<Notification[]>('/notifications');
      setNotifications(response.data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load notifications';
      setError(errorMessage);
      console.error('Failed to load notifications:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const markRead = useCallback(async (id: number) => {
    const readAt = new Date().toISOString();
    let previousReadAt: string | null = null;
    setNotifications((prev) =>
      prev.map((notification) => {
        if (notification.id !== id) return notification;
        previousReadAt = notification.readAt;
        return { ...notification, readAt };
      }),
    );

    try {
      await client.post<Notification>(`/notifications/${id}/read`);
    } catch (err) {
      console.error('Failed to mark notification read:', err);
      // The optimistic flip didn't stick, so the unread badge (derived from
      // `notifications`) shouldn't lie about it either.
      setNotifications((prev) =>
        prev.map((notification) =>
          notification.id === id ? { ...notification, readAt: previousReadAt } : notification,
        ),
      );
      throw err;
    }
  }, []);

  const deleteOne = useCallback(async (id: number) => {
    let removed: Notification | undefined;
    let removedIndex = -1;
    setNotifications((prev) => {
      removedIndex = prev.findIndex((notification) => notification.id === id);
      if (removedIndex === -1) return prev;
      removed = prev[removedIndex];
      return prev.filter((notification) => notification.id !== id);
    });

    try {
      await client.delete(`/notifications/${id}`);
    } catch (err) {
      console.error('Failed to delete notification:', err);
      // Put it back where it was rather than at the end -- the list is
      // ordered newest-due-first, and a notification restored out of place
      // would look like a different one arrived.
      if (removed !== undefined) {
        setNotifications((prev) => {
          const next = [...prev];
          next.splice(removedIndex, 0, removed as Notification);
          return next;
        });
      }
      throw err;
    }
  }, []);

  const clearAll = useCallback(async () => {
    await client.delete('/notifications');
    setNotifications([]);
  }, []);

  const addPushedNotification = useCallback((notification: Notification) => {
    setNotifications((prev) => [notification, ...prev.filter((n) => n.id !== notification.id)]);
  }, []);

  const unreadCount = useMemo(
    () => notifications.filter((notification) => !notification.readAt).length,
    [notifications],
  );

  const value = useMemo<NotificationsContextType>(
    () => ({
      notifications,
      unreadCount,
      isLoading,
      error,
      loadNotifications,
      markRead,
      deleteOne,
      clearAll,
      addPushedNotification,
    }),
    [
      notifications,
      unreadCount,
      isLoading,
      error,
      loadNotifications,
      markRead,
      deleteOne,
      clearAll,
      addPushedNotification,
    ],
  );

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useNotifications = (): NotificationsContextType => {
  const context = useContext(NotificationsContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationsProvider');
  }
  return context;
};
